<template>
  <transition name="modal">
    <div
      v-if="open"
      class="contact-modal"
      @click.self="$emit('close')"
    >
      <div
        class="contact-modal__panel"
        role="dialog"
        aria-modal="true"
        :aria-label="form.title"
      >
        <button
          class="contact-modal__close"
          aria-label="Fermer"
          @click="$emit('close')"
        >
          <img
            src="/assets/pictos/close.svg"
            alt=""
          >
        </button>

        <h3 class="contact-modal__title">{{ form.title }}</h3>

        <form
          v-if="status !== 'success'"
          @submit.prevent="submit"
        >
          <label class="field">
            <span>{{ form.fields.name }}</span>
            <input
              v-model.trim="values.name"
              type="text"
              name="name"
              required
            >
          </label>
          <label class="field">
            <span>{{ form.fields.company }}</span>
            <input
              v-model.trim="values.company"
              type="text"
              name="company"
            >
          </label>
          <label class="field">
            <span>{{ form.fields.email }}</span>
            <input
              v-model.trim="values.email"
              type="email"
              name="email"
              required
            >
          </label>
          <label class="field">
            <span>{{ form.fields.phone }}</span>
            <input
              v-model.trim="values.phone"
              type="tel"
              name="phone"
            >
          </label>
          <label class="field field--area">
            <span>{{ form.fields.message }}</span>
            <textarea
              v-model.trim="values.message"
              name="message"
              rows="5"
              required
            />
          </label>

          <p
            v-if="status === 'error'"
            class="contact-modal__error"
          >{{ form.error }}</p>

          <button
            class="btn-pill is-white-outline contact-modal__send"
            type="submit"
            :disabled="status === 'sending'"
          >
            <BtnStar />
            {{ status === 'sending' ? form.sending : form.send }}
          </button>
        </form>

        <p
          v-else
          class="contact-modal__success"
        >{{ form.success }}</p>
      </div>
    </div>
  </transition>
</template>

<script>
import axios from 'axios'
import BtnStar from '@/components/BtnStar.vue'
import { STRAPI_URL } from '@/content/templiersContent'

export default {
  name: 'ContactModal',
  components: { BtnStar },
  props: {
    open: {
      type: Boolean,
      default: false
    },
    data: {
      type: Object,
      required: true
    }
  },
  emits: ['close'],
  data() {
    return {
      status: 'idle', // idle | sending | success | error
      values: { name: '', company: '', email: '', phone: '', message: '' }
    }
  },
  computed: {
    form() {
      return this.data.contact.form
    }
  },
  watch: {
    open(isOpen) {
      document.body.style.overflow = isOpen ? 'hidden' : ''
      if (isOpen && this.status === 'success') this.status = 'idle'
    }
  },
  methods: {
    async submit() {
      this.status = 'sending'
      try {
        if (STRAPI_URL) {
          // Endpoint Strapi à venir (même pattern que oiko: POST /api/contact/send-email)
          await axios.post(`${STRAPI_URL}/api/contact/send-email`, {
            name: this.values.name,
            company: this.values.company,
            email: this.values.email,
            phone: this.values.phone,
            message: this.values.message
          })
        } else {
          // Pas encore de backend : bascule sur le client mail du visiteur.
          const subject = encodeURIComponent('Contact - Templiers Valley')
          const body = encodeURIComponent(
            `${this.values.message}\n\n${this.values.name}` +
            (this.values.company ? ` - ${this.values.company}` : '') +
            `\n${this.values.email}${this.values.phone ? ' - ' + this.values.phone : ''}`
          )
          window.location.href = `mailto:${this.data.contact.email}?subject=${subject}&body=${body}`
        }
        this.status = 'success'
        this.values = { name: '', company: '', email: '', phone: '', message: '' }
      } catch (err) {
        console.warn('[contact] send failed', err?.message || err)
        this.status = 'error'
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.contact-modal {
  position: fixed;
  inset: 0;
  z-index: 90;
  background: rgba($navy-dark, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.contact-modal__panel {
  position: relative;
  width: min(440px, 100%);
  max-height: calc(100vh - 40px);
  overflow-y: auto;
  background: $red;
  color: $white;
  padding: 42px 40px 36px;
}

.contact-modal__close {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 34px;
  height: 34px;
  border: none;
  background: none;
  padding: 6px;

  img {
    width: 100%;
    filter: brightness(0) invert(1);
  }
}

.contact-modal__title {
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 26px;
}

.field {
  display: flex;
  align-items: baseline;
  gap: 14px;
  margin-bottom: 18px;

  span {
    flex: 0 0 auto;
    min-width: 96px;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.18em;
    text-transform: uppercase;
  }

  input {
    flex: 1;
    background: none;
    border: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.6);
    color: $white;
    font-family: inherit;
    font-size: 15px;
    padding: 4px 2px;

    &:focus {
      outline: none;
      border-bottom-color: $white;
    }
  }

  &--area {
    flex-direction: column;
    align-items: stretch;

    textarea {
      background: $white;
      border: none;
      border-radius: 8px;
      color: $navy;
      font-family: inherit;
      font-size: 15px;
      padding: 12px;
      resize: vertical;

      &:focus {
        outline: 2px solid $yellow;
      }
    }
  }
}

.contact-modal__send {
  margin-top: 6px;

  &:disabled {
    opacity: 0.6;
    cursor: wait;
  }
}

.contact-modal__error {
  font-size: 13px;
  margin-bottom: 12px;
  font-weight: 600;
}

.contact-modal__success {
  font-size: 16px;
  font-weight: 600;
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.25s ease;

  .contact-modal__panel {
    transition: transform 0.25s ease;
  }
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;

  .contact-modal__panel {
    transform: translateY(16px);
  }
}
</style>
